// src/components/financials/CSVImportParser.js
// Updated: 02/13/2026 - Enhanced Parsing, Header Scanning, & Debug UI

import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Grid, Button, 
  Table, TableBody, TableCell, TableHead, TableRow, 
  Alert, MenuItem, TextField, CircularProgress, Chip, IconButton
} from '@mui/material';
import { CloudUpload, Delete, CheckCircle, Save, AutoFixHigh } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const CSVImportParser = () => {
  const { userProfile, currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [source, setSource] = useState('cashapp'); 
  const [parsedRows, setParsedRows] = useState([]);
  const [detectedHeaders, setDetectedHeaders] = useState([]); // Added for debugging
  const [loading, setLoading] = useState(false);
  const [batchTotal, setBatchTotal] = useState(0);

  const churchId = userProfile?.churchId;

  const handleFileRead = (e) => {
    const content = e.target.result;
    
    // FIX 1: Safely handle Mac (\r), Linux (\n), and Windows (\r\n) line endings
    const rows = content.split(/\r\n|\n|\r/).filter(r => r.trim() !== '');
    if (rows.length < 2) return;

    const data = [];
    let runningTotal = 0;

    // FIX 2: Smart Header Row Scanner (Finds headers even if bank adds junk at the top)
    let headerRowIndex = 0;
    let headers = [];
    
    for (let i = 0; i < Math.min(10, rows.length); i++) {
        const tempHeaders = rows[i].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(h => h.replace(/"/g, '').trim().toLowerCase());
        // Look for a row that has 'date' AND some variation of 'amount'
        if (tempHeaders.some(h => h.includes('date')) && tempHeaders.some(h => h.includes('amount') || h.includes('debit') || h.includes('withdrawal'))) {
            headerRowIndex = i;
            headers = tempHeaders;
            break;
        }
    }
    
    // Fallback if scanner fails
    if (headers.length === 0) {
        headers = rows[0].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(h => h.replace(/"/g, '').trim().toLowerCase());
    }

    setDetectedHeaders(headers); // Save for UI feedback

    rows.slice(headerRowIndex + 1).forEach((row, index) => {
        const cols = row.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(c => c.replace(/"/g, '').trim());
        let item = null;

        // --- STRATEGY: CASH APP ---
        if (source === 'cashapp') {
            const type = cols[2]; 
            const amount = parseFloat(cols[4]?.replace(/[$,]/g, ''));
            const status = cols[10];

            if (type === 'Payment Received' && status === 'COMPLETED') {
                item = { id: index, date: cols[1]?.split(' ')[0], donorName: cols[12] || 'Unknown CashApp Donor', amount: amount, memo: cols[11] || '', method: 'CashApp' };
            }
        }
        
        // --- STRATEGY: ZELLE (SMART MAPPER) ---
        else if (source === 'zelle') {
            const dateIdx = headers.findIndex(h => h.includes('date'));
            const nameIdx = headers.findIndex(h => h.includes('name') || h.includes('sender') || h.includes('from'));
            const amountIdx = headers.findIndex(h => h.includes('amount'));
            const memoIdx = headers.findIndex(h => h.includes('memo') || h.includes('note') || h.includes('reason') || h.includes('description'));
            const statusIdx = headers.findIndex(h => h.includes('status'));

            if (dateIdx === -1 || amountIdx === -1) return;

            const rawAmount = cols[amountIdx];
            const amount = parseFloat(rawAmount?.replace(/[$,]/g, ''));
            const status = statusIdx !== -1 ? cols[statusIdx]?.toLowerCase() : 'completed';
            
            if (!isNaN(amount) && amount > 0 && !status.includes('fail') && !status.includes('cancel')) {
                item = { id: index, date: cols[dateIdx], donorName: nameIdx !== -1 ? cols[nameIdx] : 'Unknown Zelle Donor', amount: amount, memo: memoIdx !== -1 ? cols[memoIdx] : '', method: 'Zelle' };
            }
        }

        // --- STRATEGY: GENERIC / STRIPE ---
        else if (source === 'stripe') {
            item = { id: index, date: cols[0], donorName: cols[1], amount: parseFloat(cols[2]?.replace(/[$,]/g, '')), memo: cols[3] || '', method: 'Online' };
        }

        // --- STRATEGY: EXPENSE CSV ---
        else if (source === 'expense_csv') {
            const dateIdx = headers.findIndex(h => h.includes('date'));
            
            // FIX 3: Expanded column detections
            const payeeIdx = headers.findIndex(h => h.includes('payee') || h.includes('vendor') || h.includes('description') || h.includes('name') || h.includes('merchant'));
            
            let amountIdx = headers.findIndex(h => h.includes('amount'));
            if (amountIdx === -1) amountIdx = headers.findIndex(h => h.includes('debit'));
            if (amountIdx === -1) amountIdx = headers.findIndex(h => h.includes('withdrawal'));
            
            const categoryIdx = headers.findIndex(h => h.includes('category') || h.includes('type'));

            if (dateIdx !== -1 && amountIdx !== -1) {
                const rawAmount = cols[amountIdx];
                const amount = Math.abs(parseFloat(rawAmount?.replace(/[$,]/g, ''))); // Force positive
                
                if (!isNaN(amount) && amount > 0) {
                    item = {
                        id: index,
                        date: cols[dateIdx],
                        payee: payeeIdx !== -1 ? cols[payeeIdx] : 'Unknown Vendor',
                        amount: amount,
                        category: categoryIdx !== -1 ? cols[categoryIdx] : 'Uncategorized',
                        method: 'CSV Import'
                    };
                }
            }
        }

        if (item && !isNaN(item.amount) && item.amount > 0) {
            data.push(item);
            runningTotal += item.amount;
        }
    });

    setParsedRows(data);
    setBatchTotal(runningTotal);
  };

  const handleUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      setDetectedHeaders([]); // Clear previous debug info
      const reader = new FileReader();
      reader.onload = handleFileRead;
      reader.readAsText(uploadedFile);
    }
  };

  const handleRemoveRow = (id) => {
    const updated = parsedRows.filter(r => r.id !== id);
    setParsedRows(updated);
    setBatchTotal(updated.reduce((sum, r) => sum + r.amount, 0));
  };

  const handleImport = async () => {
    if (parsedRows.length === 0) return;
    setLoading(true);

    try {
        const batchRef = await addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'batches'), {
            date: new Date().toISOString().split('T')[0],
            name: `${source.toUpperCase()} Import - ${new Date().toLocaleDateString()}`,
            totalAmount: batchTotal,
            itemCount: parsedRows.length,
            status: 'posted', 
            source: source,
            createdBy: currentUser.uid,
            createdAt: serverTimestamp()
        });

        const collectionPath = source === 'expense_csv' ? 'expenses' : 'transactions';
        
        const txPromises = parsedRows.map(row => {
            if (source === 'expense_csv') {
                return addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'expenses'), {
                    batchId: batchRef.id,
                    date: row.date,
                    amount: row.amount,
                    payee: row.payee,
                    category: row.category,
                    fundId: 'general', 
                    description: `Imported via CSV Batch #${batchRef.id}`,
                    status: 'approved', 
                    createdAt: serverTimestamp(),
                    createdBy: currentUser.uid
                });
            } else {
                return addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'transactions'), {
                    batchId: batchRef.id,
                    date: row.date,
                    amount: row.amount,
                    donorName: row.donorName,
                    donorId: '', 
                    method: row.method,
                    fundId: 'general', 
                    splits: [{ fundName: 'General', amount: row.amount }],
                    note: row.memo,
                    status: 'posted',
                    createdAt: serverTimestamp(),
                    createdBy: currentUser.uid
                });
            }
        });

        await Promise.all(txPromises);
        
        alert(`Success! Imported ${parsedRows.length} items.`);
        setParsedRows([]);
        setFile(null);
        setBatchTotal(0);
        setDetectedHeaders([]);

    } catch (e) {
        console.error(e);
        alert("Import Failed: " + e.message);
    }
    setLoading(false);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Data Import Tool</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Upload CSV exports to batch create transactions or expenses.
        </Typography>

        <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={4}>
                <TextField 
                    select fullWidth label="Source Platform" 
                    value={source} onChange={(e) => {
                        setSource(e.target.value);
                        setParsedRows([]); 
                        setFile(null);
                        setDetectedHeaders([]);
                    }}
                >
                    <MenuItem value="cashapp">Cash App (Activity CSV)</MenuItem>
                    <MenuItem value="zelle">Zelle (Activity Report)</MenuItem>
                    <MenuItem value="stripe">Stripe / Generic Income</MenuItem>
                    <MenuItem value="expense_csv">General Expense CSV</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
                <Button 
                    variant="outlined" 
                    component="label" 
                    fullWidth
                    startIcon={<CloudUpload />}
                    sx={{ height: 56 }}
                >
                    {file ? file.name : "Select CSV File"}
                    <input type="file" hidden accept=".csv" onChange={handleUpload} />
                </Button>
            </Grid>
            <Grid item xs={12} md={4}>
                <Button 
                    variant="contained" 
                    fullWidth 
                    size="large"
                    onClick={handleImport}
                    disabled={parsedRows.length === 0 || loading}
                    startIcon={<Save />}
                    sx={{ height: 56 }}
                >
                    {loading ? <CircularProgress size={24} /> : `Import ${parsedRows.length} Items`}
                </Button>
            </Grid>
        </Grid>

        {source === 'expense_csv' && (
            <Alert severity="info" sx={{ mt: 2, fontSize: '0.9rem' }} icon={<AutoFixHigh />}>
                <strong>Expense CSV Tip:</strong> Ensure your CSV has columns like "Date", "Description/Payee", and "Amount/Debit".
            </Alert>
        )}

        {/* FEEDBACK UI: Let the user know exactly why the file failed to import */}
        {file && parsedRows.length === 0 && detectedHeaders.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
                <strong>No valid data rows found!</strong> The system looked for columns named "date" and "amount" (or debit/withdrawal) but couldn't process the rows.
                <br /><br />
                <strong>Headers found in your file:</strong> <span style={{ fontFamily: 'monospace' }}>{detectedHeaders.join(', ')}</span>
                <br /><br />
                <em>Fix: Open your file in Excel and ensure the header row exists and the columns are cleanly named.</em>
            </Alert>
        )}

        {parsedRows.length > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
                <strong>Ready to Import:</strong> Found {parsedRows.length} valid records totaling <strong>${batchTotal.toFixed(2)}</strong>.
            </Alert>
        )}
      </Paper>

      {parsedRows.length > 0 && (
          <Paper sx={{ p: 0, overflow: 'hidden' }}>
              <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>{source === 'expense_csv' ? 'Payee' : 'Donor'}</TableCell>
                          <TableCell>{source === 'expense_csv' ? 'Category' : 'Memo'}</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="center">Action</TableCell>
                      </TableRow>
                  </TableHead>
                  <TableBody>
                      {parsedRows.map((row) => (
                          <TableRow key={row.id} hover>
                              <TableCell>{row.date}</TableCell>
                              <TableCell>{source === 'expense_csv' ? row.payee : row.donorName}</TableCell>
                              <TableCell>
                                  {(source === 'expense_csv' ? row.category : row.memo) && (
                                      <Chip label={source === 'expense_csv' ? row.category : row.memo} size="small" />
                                  )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', color: source === 'expense_csv' ? 'error.main' : 'success.main' }}>
                                  ${row.amount.toFixed(2)}
                              </TableCell>
                              <TableCell align="center">
                                  <IconButton size="small" color="error" onClick={() => handleRemoveRow(row.id)}>
                                      <Delete fontSize="small" />
                                  </IconButton>
                              </TableCell>
                          </TableRow>
                      ))}
                  </TableBody>
              </Table>
          </Paper>
      )}
    </Box>
  );
};

export default CSVImportParser;